import { Component, Host, Prop, h } from '@stencil/core';
import { getTenant } from '../../helpers/helpers';
import { endpoints } from '../../../.conf/resources';
import type { CssClassMap } from '../../helpers/utils/interfaces';

@Component({
  tag: 'cms-badge',
  styleUrls: {
    hb: 'cms-badge-hb.scss',
    boh: 'cms-badge-boh.scss',
  },
  // styleUrl: 'cms-badge.scss',
  shadow: true,
})
export class CmsBadge {
  private readonly badgeBgImage = 'badge-bg.webp';
  private readonly imageSrc = `${endpoints.assetPath}/${this.badgeBgImage}`;

  /**
   * Displayed the Badge Name as Text
   */
  @Prop() name: string;

  /**
   * Displayed functional close icon
   */
  @Prop() withCloseIcon = false;

  /**
   * Specifies where to display the linked URL.
   * Only applies when an `href` is provided.
   * Special keywords: `"_blank"`, `"_self"`, `"_parent"`, `"_top"`.
   */
  @Prop() target: '_blank' | '_self' | '_parent' | '_top';

  /**
   * Contains a URL or a URL fragment that the hyperlink points to.
   * If this property is set, an anchor tag will be rendered.
   */
  @Prop() href: string | undefined;

  /**
   * set icon inside badge like notificationInfo
   */
  @Prop() icon: string | undefined;

  /**
   * set icon size inside badge like sm, md, lg
   */
  @Prop() iconSize: string | undefined;

  /**
   * set icon color inside badge as css property
   */
  @Prop() iconColor: string | undefined;

  /**
   * set the badge theme color
   */
  @Prop({ reflect: true }) theme?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'service';

  /**
   * set the badge size
   */
  @Prop({ reflect: true }) size?: 'badge-sm' | 'badge-md';

  private renderBadgeContent() {
    if (this.name === undefined || this.name === '') {
      return;
    }

    if (this.withCloseIcon) {
      return this.renderBadgeWithIcon();
    }

    if (this.href) {
      return this.renderBadgeLink();
    }

    if (this.icon) {
      return this.renderBadgeWithIconLeft();
    }

    return this.renderDefaultBadge();
  }

  private renderScratchLeft() {
    if (getTenant() !== 'hornbach') {
      return;
    }
    return (
      <span
        style={{
          maskImage: `url(${this.imageSrc})`,
          webkitMaskImage: `url(${this.imageSrc})`,
        }}
        class={'scratch-left scratch'}
      />
    );
  }

  private renderScratchRight() {
    if (getTenant() !== 'hornbach') {
      return;
    }
    return (
      <span
        style={{
          maskImage: `url(${this.imageSrc})`,
          webkitMaskImage: `url(${this.imageSrc})`,
        }}
        class={'scratch-right scratch'}
      />
    );
  }

  private renderDefaultBadge() {
    return (
      <div class={'badge-inner'}>
        {this.renderScratchLeft()}
        <span class={'badge-name'}>{this.name}</span>
        {this.renderScratchRight()}
      </div>
    );
  }

  private renderBadgeWithIcon() {
    return (
      <div class={'badge-inner'}>
        {this.renderScratchLeft()}
        <span class={'badge-name'}>{this.name}</span>
        <button title='close' type='button' class={'close-button'}>
          <cms-icon size='sm' color-token={this.iconColor} icon='close' />
        </button>
        {this.renderScratchRight()}
      </div>
    );
  }

  private renderBadgeWithIconLeft() {
    return (
      <div class={'badge-inner'}>
        {this.renderScratchLeft()}
        <div class={'icon-wrapper'}>
          <cms-icon size={this.iconSize} icon={this.icon} />
        </div>
        <span class={'badge-name'}>{this.name}</span>
        {this.renderScratchRight()}
      </div>
    );
  }

  private renderBadgeLink() {
    return (
      <div class={'badge-inner'}>
        {this.renderScratchLeft()}
        <a
          class={'badge-link'}
          href={this.href}
          title='hornbach'
          target={this.target}
        >
          <span class={'badge-name'}>{this.name}</span>
        </a>
        {this.renderScratchRight()}
      </div>
    );
  }

  private hostClassMap(): CssClassMap {
    return {
      'close-icon': this.withCloseIcon,
      'width-icon': this.icon !== undefined,
      [this.size]: this.size !== undefined,
      [this.theme]: this.theme !== undefined,
      default: this.href === undefined && !this.withCloseIcon,
    };
  }

  render() {
    return <Host class={this.hostClassMap()}>{this.renderBadgeContent()}</Host>;
  }
}
